import { MonthNumber } from '../../core/model/const/enums';
import { LocalDate } from '../../core/model/local-date';
import { ModelBase } from '../../core/model/model-base';
import { Currency, isMoneyLike, Money } from '../../core/model/money';
import { Translation } from '../../core/model/translation';
import { LettingAdvertismentPlatformType } from './const/enums';
import { LettingFeatureSelection } from './letting-feature-selection';

export class LettingAdvertisment extends ModelBase {
  constructor(data?: any) {
    super();
    if (data) {
      this.id = data.id;
      this.rentalObjectId = data.rentalObjectId;
      this.buildingId = data.buildingId;
      this.rentalObjectName = data.rentalObjectName;
      this.platform = data.platform;
      this.createdAt = data.createdAt;
      this.approvedAt = data.approvedAt;
      this.closedAt = data.closedAt;
      this.cancelledAt = data.cancelledAt;
      this.vacancyFrom = LocalDate.fromDate(data.vacancyFrom);
      this.vacancyTo = LocalDate.fromDate(data.vacancyTo);
      this.monthlyRent = isMoneyLike(data.monthlyRent)
        ? new Money(data.monthlyRent.currencyCode, data.monthlyRent.amount)
        : new Money(Currency.SEK, 0);
      this.yearlyRent = isMoneyLike(data.yearlyRent)
        ? new Money(data.yearlyRent.currencyCode, data.yearlyRent.amount)
        : new Money(Currency.SEK, 0);
      this.monthsWithRent = data.monthsWithRent;
      this.publishFrom = data.publishFrom;
      this.publishTo = data.publishTo;
      this.objectDescription = data.objectDescription;
      this.buildingDescription = data.buildingDescription;
      this.buildingAreaDescription = data.buildingAreaDescription;
      this.boplatsSverigeLocation = data.boplatsSverigeLocation;
      this.boplatsSverigeMunicipality = data.boplatsSverigeMunicipality;
      this.boplatsSverigeLivingArea = data.boplatsSverigeLivingArea;
      this.boplatsSverigeRooms = data.boplatsSverigeRooms;
      this.boplatsSverigeStreetAddress = data.boplatsSverigeStreetAddress;
      this.boplatsSverigeDescriptionURL = data.boplatsSverigeDescriptionURL;
      this.sentToPlatformAt = data.sentToPlatformAt;
      this.cancelledOnPlatformAt = data.cancelledOnPlatformAt;

      this.buildingCommonAreasPhotos = Array.isArray(data.buildingCommonAreasPhotos) ? data.buildingCommonAreasPhotos : [];
      this.buildingSurroundingsPhotos = Array.isArray(data.buildingSurroundingsPhotos) ? data.buildingSurroundingsPhotos : [];
      this.buildingFacadePhotos = Array.isArray(data.buildingFacadePhotos) ? data.buildingFacadePhotos : [];
      this.buildingSalesDocuments = Array.isArray(data.buildingSalesDocuments) ? data.buildingSalesDocuments : [];
      this.floorPlanDocuments = Array.isArray(data.floorPlanDocuments) ? data.floorPlanDocuments : [];
      this.rentalObjectDocuments = Array.isArray(data.rentalObjectDocuments) ? data.rentalObjectDocuments : [];

      this.uniqueObjectDescription = data.uniqueObjectDescription;
      this.uniqueBuildingDescription = data.uniqueBuildingDescription;
      this.uniqueBuildingAreaDescription = data.uniqueBuildingAreaDescription;

      this.homeqRequirementProfile = data.homeqRequirementProfile;
      this.homeqIsNewProduction = data.homeqIsNewProduction;

      this.dhMarketRequirementProfile = data.dhMarketRequirementProfile;
      this.dhMarketArea = data.dhMarketArea;
      this.dhMarketAreaName = data.dhMarketAreaName;
      this.dhMarketAreaDescription = data.dhMarketAreaDescription;
      this.dhMarketAreaSearchTags = data.dhMarketAreaSearchTags;

      this.availableLettingFeatures = data.availableLettingFeatures?.map(feature => new LettingFeatureSelection(feature)) ?? [];
      this.includedLettingFeatures = data.includedLettingFeatures?.map(feature => new LettingFeatureSelection(feature)) ?? [];
      this.notIncludedLettingFeatures = data.notIncludedLettingFeatures?.map(feature => new LettingFeatureSelection(feature)) ?? [];

      this.showingTenantId = data.showingTenantId || null;
      this.showingTenantText = data.showingTenantText || '';
      this.showingOrdinaryId = data.showingOrdinaryId || null;
      this.showingOrdinaryText = data.showingOrdinaryText || '';

      this.marketSendSubscriptions = data.marketSendSubscriptions ?? false;
      this.marketPublishToBoplatsSverige = data.marketPublishToBoplatsSverige ?? false;
    }
  }

  toString(culture?: string): string {
    return `${this.rentalObjectName}, ${LocalDate.fromDate(this.publishFrom).toLocaleDateString(culture)} - ${LocalDate.fromDate(
      this.publishTo
    ).toLocaleDateString(culture)}`;
  }

  get platformName(): string {
    switch (this.platform) {
      case LettingAdvertismentPlatformType.HomeQ:
        return 'LETTING.ADVERTISMENT.PLATFORMS.HOMEQ';
      case LettingAdvertismentPlatformType.DhMarket:
        return 'LETTING.ADVERTISMENT.PLATFORMS.DHMARKET';
      case LettingAdvertismentPlatformType.BoplatsSverige:
        return 'LETTING.ADVERTISMENT.PLATFORMS.BOPLATS_SVERIGE';
      default:
        return '';
    }
  }

  get isSentToPlatform(): boolean {
    return !!this.sentToPlatformAt;
  }

  get platformState(): string {
    if (this.cancelledOnPlatformAt) {
      return 'LETTING.ADVERTISMENT.STATES.UNPUBLISHED';
    }

    if (this.sentToPlatformAt) {
      return 'LETTING.ADVERTISMENT.STATES.PUBLISHED';
    }

    return 'LETTING.ADVERTISMENT.STATES.DRAFT';
  }

  toJSON() {
    return {
      id: this.id,
      rentalObjectId: this.rentalObjectId,
      platform: this.platform,
      createdAt: this.createdAt,
      approvedAt: this.approvedAt,
      closedAt: this.closedAt,
      cancelledAt: this.cancelledAt,
      publishFrom: this.publishFrom,
      publishTo: this.publishTo,
      vacancyFrom: this.vacancyFrom,
      vacancyTo: this.vacancyTo,
      monthlyRent: this.monthlyRent,
      yearlyRent: this.yearlyRent,
      monthsWithRent: this.monthsWithRent,
      objectDescription: this.objectDescription,
      buildingDescription: this.buildingDescription,
      buildingAreaDescription: this.buildingAreaDescription,
      uniqueRentalObjectDescription: this.uniqueObjectDescription,
      homeqRequirementProfile: this.homeqRequirementProfile,
      homeqIsNewProduction: this.homeqIsNewProduction,
      dhMarketRequirementProfile: this.dhMarketRequirementProfile,
      dhMarketArea: this.dhMarketArea,
      dhMarketAreaName: this.dhMarketAreaName,
      dhMarketAreaDescription: this.dhMarketAreaDescription,
      dhMarketAreaSearchTags: this.dhMarketAreaSearchTags,
      includedLettingFeatureIds: this.includedLettingFeatureIds,
      notIncludedLettingFeatureIds: this.notIncludedLettingFeatureIds,
      buildingCommonAreasPhotos: this.buildingCommonAreasPhotos,
      buildingFacadePhotos: this.buildingFacadePhotos,
      buildingSurroundingsPhotos: this.buildingSurroundingsPhotos,
      buildingSalesDocuments: this.buildingSalesDocuments,
      rentalObjectDocuments: this.rentalObjectDocuments,
      floorPlanDocuments: this.floorPlanDocuments,
      showingTenantId: this.showingTenantId,
      showingTenantText: this.showingTenantText,
      showingOrdinaryId: this.showingOrdinaryId,
      showingOrdinaryText: this.showingOrdinaryText,
      marketSendSubscriptions: this.marketSendSubscriptions,
      marketPublishToBoplatsSverige: this.marketPublishToBoplatsSverige,
      boplatsSverigeDescriptionURL: this.boplatsSverigeDescriptionURL,
    };
  }

  buildingCommonAreasPhotos: any[];
  buildingFacadePhotos: any[];
  buildingSurroundingsPhotos: any[];
  buildingSalesDocuments: any[];
  rentalObjectDocuments: any[];
  floorPlanDocuments: any[];

  rentalObjectId: string;
  buildingId: string;
  rentalObjectName: string;
  platform: LettingAdvertismentPlatformType;
  createdAt: Date;
  approvedAt: Date;
  closedAt: Date;
  cancelledAt: Date;
  publishFrom: string;
  publishTo: string;
  vacancyFrom: LocalDate;
  vacancyTo: LocalDate;
  currencyCode: string;
  monthlyRent: Money;
  yearlyRent: Money;
  monthsWithRent: MonthNumber[];
  objectDescription: Translation[];
  buildingDescription: string;
  buildingAreaDescription: string;
  boplatsSverigeLocation: string;
  boplatsSverigeMunicipality: string;
  boplatsSverigeLivingArea: number;
  boplatsSverigeRooms: number;
  boplatsSverigeStreetAddress: string;
  boplatsSverigeDescriptionURL: string;
  uniqueObjectDescription: boolean;
  uniqueBuildingDescription: boolean;
  uniqueBuildingAreaDescription: boolean;
  sentToPlatformAt: Date;
  cancelledOnPlatformAt: Date;

  homeqRequirementProfile: number;
  homeqIsNewProduction: boolean;

  dhMarketRequirementProfile: string;
  dhMarketArea: string;
  dhMarketAreaName: string;
  dhMarketAreaDescription: Translation[];
  dhMarketAreaSearchTags: string[];

  availableLettingFeatures: LettingFeatureSelection[] = [];
  includedLettingFeatures: LettingFeatureSelection[] = [];
  notIncludedLettingFeatures: LettingFeatureSelection[] = [];
  includedLettingFeatureIds: string[] = [];
  notIncludedLettingFeatureIds: string[] = [];

  showingTenantId: string;
  showingTenantText: string;
  showingOrdinaryId: string;
  showingOrdinaryText: string;

  marketSendSubscriptions: boolean;
  marketPublishToBoplatsSverige: boolean;
}
